import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { HttpStatusCode, type AxiosError } from 'axios';
import {
  OtpRateLimitStateReasonEnum,
  VerifyOtpIncorrectResponseReasonEnum,
  type OtpRateLimitState,
  type VerifyOtpIncorrectResponse,
  type CognitoSignInSuccessResponse,
} from '@bvengineering/cas-member-gateway-service-client';
import { useToast } from '@/primer/components';
import {
  useInitiateOtp,
  usePostAuthenticateInvite,
  useVerifyOtp,
  type InitiateOtpResponse,
} from '@/api';
import { useAuth } from '@/api/hooks/useAuth';
import { AccountLockout, InviteErrorScreen, InviteErrorVariant } from '@/components/ui';
import { PageLayout } from '@/components/layout';
import { getBlockedDeadline } from '@/utils/inviteLockout';
import { useAcceptInvite, type PostAcceptDestination } from '@/hooks/useAcceptInvite';
import {
  classifyPostAuthenticateError,
  clearOtpChallenge,
  InviteAuthFailure,
  readOtpAttemptsRemaining,
  readOtpChallenge,
  readOtpEmail,
  storeOtpChallenge,
  MS_PER_SECOND,
  OTP_MAX_ATTEMPTS,
  OTP_RESEND_COOLDOWN_MS,
  OTP_STATE,
  OTP_TOAST,
  type OtpState,
} from '@/utils';
import { InviteOtpView } from './InviteOtpView';

type OtpFailure = VerifyOtpIncorrectResponse | OtpRateLimitState | { reason?: undefined };

type Destination = PostAcceptDestination | { to: '/invite'; search: { token: string } };

function readFailure(error: unknown) {
  const response = (error as AxiosError<OtpFailure>)?.response;
  return { status: response?.status, failure: response?.data };
}

function isBlockedRateLimit(body: OtpFailure | undefined): body is OtpRateLimitState {
  return body?.reason === OtpRateLimitStateReasonEnum.Blocked;
}

export function InviteOtp() {
  const { token } = useSearch({ from: '/_public/invite' });
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { completeOtpSignIn, isAuthenticated } = useAuth();

  const verifyOtp = useVerifyOtp();
  const postAuthenticate = usePostAuthenticateInvite();
  const initiateOtp = useInitiateOtp();
  const { acceptAndRoute: acceptInvite } = useAcceptInvite(token);

  const [challenge, setChallenge] = useState(() => readOtpChallenge(token));
  const [email] = useState(() => readOtpEmail(token));
  const [attemptsRemaining, setAttemptsRemaining] = useState(() => readOtpAttemptsRemaining(token));
  const [state, setState] = useState<OtpState>(() =>
    attemptsRemaining === 0 ? OTP_STATE.ATTEMPTS_EXHAUSTED : OTP_STATE.ENTRY
  );

  const [resendExpiresAt, setResendExpiresAt] = useState(() => Date.now() + OTP_RESEND_COOLDOWN_MS);
  const [retryExpiresAt, setRetryExpiresAt] = useState(0);
  const [inputResetKey, setInputResetKey] = useState(0);
  const resetInput = () => setInputResetKey((key) => key + 1);
  const [destination, setDestination] = useState<Destination | null>(null);
  const [authFailure, setAuthFailure] = useState<InviteAuthFailure | null>(null);
  const [lockoutExpiresAt, setLockoutExpiresAt] = useState(0);

  // Navigate once the auth context has settled.
  useEffect(() => {
    if (destination && isAuthenticated) void navigate({ ...destination, replace: true });
  }, [destination, isAuthenticated, navigate]);

  const lockOut = (expiresAt: number) => {
    clearOtpChallenge();
    setLockoutExpiresAt(expiresAt);
    setState(OTP_STATE.BLOCKED);
  };

  const rejectIncorrectCode = (replacement: InitiateOtpResponse) => {
    const next = Math.max(0, attemptsRemaining - 1);
    setChallenge(replacement);
    storeOtpChallenge(token, replacement, { email, attemptsRemaining: next });
    setAttemptsRemaining(next);
    setState(next ? OTP_STATE.INCORRECT : OTP_STATE.ATTEMPTS_EXHAUSTED);
    resetInput();
    showToast(OTP_TOAST.INCORRECT_CODE);
  };

  // Cognito dropped the challenge session, so the creator starts over from the email step.
  const restartFromEmail = () => {
    clearOtpChallenge();
    setChallenge(null);
  };

  const handleVerifyFailure = (error: unknown) => {
    const { status, failure } = readFailure(error);

    if (status === HttpStatusCode.TooManyRequests && isBlockedRateLimit(failure)) {
      try {
        lockOut(getBlockedDeadline(HttpStatusCode.TooManyRequests, failure) ?? Date.now());
      } catch {
        setState(OTP_STATE.ERROR);
        resetInput();
      }
    } else if (
      status === HttpStatusCode.BadRequest &&
      failure?.reason === VerifyOtpIncorrectResponseReasonEnum.IncorrectCode
    ) {
      rejectIncorrectCode({ session: failure.session, challengeName: failure.challengeName });
    } else if (
      status === HttpStatusCode.TooManyRequests ||
      (status === HttpStatusCode.BadRequest && attemptsRemaining <= 1)
    ) {
      // Non-BLOCKED 429 (MAX_LIMITS_REACHED) or final-attempt 400 — per-code budget exhausted.
      setAttemptsRemaining(0);
      if (challenge) storeOtpChallenge(token, challenge, { email, attemptsRemaining: 0 });
      setState(OTP_STATE.ATTEMPTS_EXHAUSTED);
      resetInput();
    } else if (status === HttpStatusCode.BadRequest) {
      // Session error with attempts remaining (e.g. ERROR_VERIFYING_OTP) — restart from email.
      restartFromEmail();
    } else {
      setState(OTP_STATE.ERROR);
      resetInput();
    }
  };

  const handleResendFailure = (error: unknown, previousState: OtpState) => {
    const { status, failure } = readFailure(error);
    const rateLimit = status === HttpStatusCode.TooManyRequests ? failure : undefined;

    switch (rateLimit?.reason) {
      case OtpRateLimitStateReasonEnum.Blocked:
        try {
          lockOut(getBlockedDeadline(HttpStatusCode.TooManyRequests, rateLimit) ?? Date.now());
        } catch {
          setState(previousState);
          showToast(OTP_TOAST.REQUEST_FAILED);
        }
        break;
      case OtpRateLimitStateReasonEnum.SendLimited:
        setRetryExpiresAt(Date.now() + rateLimit.retryAfterSeconds * MS_PER_SECOND);
        setState(OTP_STATE.SEND_LIMITED);
        showToast(OTP_TOAST.TOO_MANY_REQUESTS);
        break;
      case OtpRateLimitStateReasonEnum.Cooldown: {
        const expiresAt = Date.now() + rateLimit.retryAfterSeconds * MS_PER_SECOND;
        setResendExpiresAt(expiresAt);
        if (previousState === OTP_STATE.SEND_LIMITED) setRetryExpiresAt(expiresAt);
        setState(previousState);
        break;
      }
      default:
        setState(previousState);
        showToast(OTP_TOAST.REQUEST_FAILED);
    }
  };

  const goToEmailStep = () =>
    void navigate({ to: '/invite/email', search: { token }, replace: true });

  const handleAuthFailure = (failure: InviteAuthFailure) => {
    showToast(OTP_TOAST.REQUEST_FAILED);
    setAuthFailure(failure);
  };

  /** Accepts the invite for the signed-in creator; on failure, sends them back to the invite page. */
  const acceptOrRecover = async (): Promise<Destination> => {
    try {
      return await acceptInvite();
    } catch (error) {
      console.error('Failed to accept invite:', error instanceof Error ? error.message : error);
      showToast(OTP_TOAST.REQUEST_FAILED);
      // Signed in already, so the invite page can accept it from its own button.
      return { to: '/invite', search: { token } };
    }
  };

  const handleComplete = async (code: string) => {
    if (!challenge || verifyOtp.isPending || state === OTP_STATE.BLOCKED) return;

    setState(OTP_STATE.VERIFYING);
    let tokens: CognitoSignInSuccessResponse;
    try {
      tokens = await verifyOtp.mutateAsync({
        magicToken: token,
        request: { session: challenge.session, code, email },
      });
    } catch (error) {
      handleVerifyFailure(error);
      return;
    }

    clearOtpChallenge();
    try {
      await postAuthenticate.mutateAsync({ magicToken: token, accessToken: tokens.accessToken });
      await completeOtpSignIn(tokens);
    } catch (error) {
      // Message only: the full axios error carries the Authorization header.
      console.error(
        'OTP authentication failed after verify:',
        error instanceof Error ? error.message : error
      );
      handleAuthFailure(classifyPostAuthenticateError(error));
      return;
    }

    setDestination(await acceptOrRecover());
  };

  const handleResend = async () => {
    const isCoolingDown = Date.now() < resendExpiresAt || Date.now() < retryExpiresAt;
    if (
      initiateOtp.isPending ||
      verifyOtp.isPending ||
      state === OTP_STATE.BLOCKED ||
      isCoolingDown
    )
      return;

    const previousState = state;
    setState(OTP_STATE.VERIFYING);
    try {
      const nextChallenge = await initiateOtp.mutateAsync({ magicToken: token, email });
      setChallenge(nextChallenge);
      storeOtpChallenge(token, nextChallenge, { email });
      setAttemptsRemaining(OTP_MAX_ATTEMPTS);
      setResendExpiresAt(Date.now() + OTP_RESEND_COOLDOWN_MS);
      setState(OTP_STATE.ENTRY);
      resetInput();
      showToast(OTP_TOAST.CODE_SENT);
    } catch (error) {
      handleResendFailure(error, previousState);
    }
  };

  if (state === OTP_STATE.BLOCKED) {
    return (
      <PageLayout className="py-8" gridProps={{ className: 'px-5' }}>
        <AccountLockout
          expiresAt={lockoutExpiresAt}
          onExpire={() => void navigate({ to: '/invite/auth', search: { token }, replace: true })}
        />
      </PageLayout>
    );
  }

  if (authFailure === InviteAuthFailure.INVALID_INVITE) {
    return <InviteErrorScreen variant={InviteErrorVariant.GENERIC} />;
  }

  if (authFailure === InviteAuthFailure.CONFLICT) {
    return <InviteErrorScreen variant={InviteErrorVariant.SIGN_IN_FAILED} />;
  }

  // The verified code is used up, so retrying means starting again from the email step.
  if (authFailure) {
    return (
      <InviteErrorScreen variant={InviteErrorVariant.BAD_REQUEST} onBack={() => goToEmailStep()} />
    );
  }

  if (!challenge) {
    return <Navigate to="/invite/email" search={{ token }} replace />;
  }

  return (
    <InviteOtpView
      state={state}
      attemptsRemaining={attemptsRemaining}
      resendExpiresAt={resendExpiresAt}
      retryExpiresAt={retryExpiresAt}
      inputResetKey={inputResetKey}
      onComplete={(code) => void handleComplete(code)}
      onResend={() => void handleResend()}
    />
  );
}

export default InviteOtp;
